// Audit viewer (F6 client half). Newest-first, permission-gated by audit:read.
import { Fragment, useEffect, useState } from 'react';
import { type AuditEntry, type AuditListResponse, formatDateTime } from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { t } from '../../i18n/index.ts';
import { Card, PageHeader } from '../../ui/primitives.tsx';

export function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<AuditListResponse>('/audit?limit=100')
      .then((r) => setEntries(r.entries))
      .catch(() => setError('Failed to load audit log'));
  }, []);

  if (error) return <p className="text-status-urgent-fg">{error}</p>;

  return (
    <div>
      <PageHeader title={t('audit.title')} subtitle={t('audit.subtitle')} />
      <Card className="overflow-x-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-3 font-medium">When</th>
              <th className="px-4 py-3 font-medium">Actor</th>
              <th className="px-4 py-3 font-medium">Action</th>
              <th className="px-4 py-3 font-medium">Entity</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted">
                  No audit entries yet.
                </td>
              </tr>
            )}
            {entries.map((e) => (
              <Fragment key={e.id}>
                <tr className="border-b border-line hover:bg-surface-2">
                  <td className="px-4 py-2 font-mono text-xs">{formatDateTime(e.at)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.actorRole ?? '-'}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {e.entityType}
                    {e.entityId ? `:${e.entityId}` : ''}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-xs text-accent hover:underline"
                      onClick={() => setOpen(open === e.id ? null : e.id)}
                    >
                      {open === e.id ? 'hide' : 'diff'}
                    </button>
                  </td>
                </tr>
                {open === e.id && (
                  <tr className="bg-surface-2">
                    <td colSpan={5} className="px-4 py-3">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <pre className="overflow-x-auto rounded bg-surface p-3 font-mono text-xs">
                          before: {JSON.stringify(e.before, null, 2)}
                        </pre>
                        <pre className="overflow-x-auto rounded bg-surface p-3 font-mono text-xs">
                          after: {JSON.stringify(e.after, null, 2)}
                        </pre>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
